import { useEffect, useState, type FormEvent } from "react";
import { BookOpen, CalendarDays, CloudSun, FishSymbol, LockKeyhole, MapPin, PackageCheck, Plus, Trash2 } from "lucide-react";
import { accountApi, consumerActivityLabels, type FishingTripWorkspace } from "../../account";

type JournalPayload = {
  trip: {
    id: string;
    status: string;
    placeName: string;
    municipalityName: string | null;
    plannedDate: string;
    endDate: string;
    activity: keyof typeof consumerActivityLabels;
    rodCount: number;
    targetSpecies: string | null;
  };
  journal: {
    rating: number | null;
    conditions_notes: string;
    fishing_notes: string;
    no_catch: number;
    updated_at: string | null;
  };
  catches: Array<{
    id: string;
    species_name: string;
    quantity: number;
    released_count: number;
    kept_count: number;
    length_mm: number | null;
    weight_g: number | null;
    notes: string;
    tackle_name: string | null;
  }>;
  tackle: Array<{ id: string; name: string; packed: number; quantity: number }>;
  resultContext: {
    forecast: null | { provider: string; forDate: string | null; capturedAt: string | null; temperatureC: number | null; windSpeedKmh: number | null; weatherCode: string | null };
    hydrologyStatus: "captured" | "unavailable" | "not_requested";
    hydrologyCapturedAt: string | null;
    hydrology: null | { provider:"syke";sourceUrl:string;boundary:string;fetchedAt:string;observations:Array<{kind:string;label:string;value:number;unit:string;rawValue?:number;rawUnit?:string;referenceSystem?:string|null;observedAt:string;stale:boolean;stationName:string;municipalityName:string|null;distanceKm:number;matchBasis?:"waterbody_name"|"station_name"}> };
  };
  summary: { catches: number; released: number };
  boundary: string;
};

const day = (value: string) =>
  new Date(`${value}T12:00:00`).toLocaleDateString("fi-FI", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export function TripFishingJournal({
  tripId,
  workspace,
}: {
  tripId: string;
  workspace?: FishingTripWorkspace | null;
}) {
  const [data, setData] = useState<JournalPayload | null>(null),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [noCatch, setNoCatch] = useState(false);
  const endpoint = `/api/account/trips/${encodeURIComponent(tripId)}/journal`;
  const load = () =>
    accountApi<JournalPayload>(endpoint)
      .then(setData)
      .catch((error) =>
        setMessage(error instanceof Error ? error.message : "Päiväkirjaa ei voitu ladata."),
      );
  useEffect(() => { void load(); }, [tripId]);
  useEffect(() => {
    if (data) setNoCatch(Boolean(data.journal.no_catch));
  }, [data?.journal.no_catch]);
  const saveJournal = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setMessage("");
    const form = new FormData(event.currentTarget);
    try {
      setData(await accountApi<JournalPayload>(endpoint, { method: "PUT", body: JSON.stringify({ rating: form.get("rating") || null, conditionsNotes: form.get("conditionsNotes"), fishingNotes: form.get("fishingNotes") }) }));
      setMessage("Yksityinen reissupäiväkirja tallennettiin.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Päiväkirjaa ei voitu tallentaa."); }
    finally { setBusy(false); }
  };
  const addCatch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setMessage("");
    const formElement = event.currentTarget, form = new FormData(formElement);
    try {
      setData(await accountApi<JournalPayload>(endpoint, { method: "POST", body: JSON.stringify({ speciesName: form.get("speciesName"), quantity: Number(form.get("quantity")), releasedCount: Number(form.get("releasedCount")), keptCount: Number(form.get("keptCount")), lengthMm: form.get("lengthMm") || null, weightG: form.get("weightG") || null, tackleItemId: form.get("tackleItemId"), notes: form.get("notes") }) }));
      formElement.reset(); setMessage("Saalis lisättiin reissupäiväkirjaan.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Saalista ei voitu lisätä."); }
    finally { setBusy(false); }
  };
  const removeCatch = async (catchId: string) => {
    setBusy(true); setMessage("");
    try { setData(await accountApi<JournalPayload>(endpoint, { method: "DELETE", body: JSON.stringify({ catchId }) })); setMessage("Saalismerkintä poistettiin."); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Saalismerkintää ei voitu poistaa."); }
    finally { setBusy(false); }
  };
  const completeTrip = async () => {
    setBusy(true); setMessage("");
    try {
      await accountApi(`/api/account/trips/${encodeURIComponent(tripId)}/complete`, {
        method: "POST",
        body: JSON.stringify({ noCatch }),
      });
      window.location.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Reissua ei voitu viimeistellä.");
      setBusy(false);
    }
  };
  if (!data) return <section className="trip-journal"><div className="trip-neutral-state"><BookOpen /><span><b>{message || "Ladataan reissupäiväkirjaa…"}</b><small>Tiedot näkyvät vain sinulle.</small></span></div></section>;
  const editable = data.trip.status !== "cancelled";
  const forecastDays = workspace?.weather.forecast?.days.filter(
    (item) => item.date >= data.trip.plannedDate && item.date <= data.trip.endDate,
  ) ?? [];
  const storedForecast = data.resultContext.forecast;
  const weatherSummary = storedForecast
    ? `${storedForecast.temperatureC === null ? "Lämpötila ei tiedossa" : `${Math.round(storedForecast.temperatureC)} °C`}${storedForecast.windSpeedKmh === null ? "" : ` · tuuli ${Math.round(storedForecast.windSpeedKmh)} km/h`}`
    : forecastDays.length
    ? `${Math.round(Math.min(...forecastDays.map((item) => item.temperatureMinC)))}–${Math.round(Math.max(...forecastDays.map((item) => item.temperatureMaxC)))} °C · tuuli enintään ${Math.round(Math.max(...forecastDays.map((item) => item.windSpeedMaxKmh)))} km/h`
    : workspace?.weather.state === "past"
      ? "Ennustetta ei esitetä jälkikäteen – kirjaa toteutuneet olosuhteet itse."
      : "Sääennustetta ei ollut saatavilla tälle ajalle.";
  const packedTackle = data.tackle.filter((item) => Boolean(item.packed));
  const completed=data.trip.status==="completed";
  return (
    <section className="trip-journal" aria-labelledby="trip-journal-title">
      <header>
        <div><span className="eyebrow">Oma muisti reissulta</span><h3 id="trip-journal-title">Reissupäiväkirja</h3><p>Kirjaa olosuhteet, havainnot, saaliit ja toimineet Kalapakin välineet.</p></div>
        <div className="trip-journal-summary"><FishSymbol /><span><b>{data.summary.catches}</b><small>kalaa · {data.summary.released} vapautettu</small></span></div>
      </header>
      {message && <p className="form-message trip-message" role="status">{message}</p>}
      <section className="trip-result-context" aria-label="Reissun esitäytetyt tiedot">
        <article>
          <MapPin />
          <span><small>Paikka</small><b>{data.trip.placeName}</b><em>{data.trip.municipalityName ?? "Kunta ei tiedossa"}</em></span>
        </article>
        <article>
          <CalendarDays />
          <span><small>Ajankohta ja tapa</small><b>{data.trip.plannedDate === data.trip.endDate ? day(data.trip.plannedDate) : `${day(data.trip.plannedDate)}–${day(data.trip.endDate)}`}</b><em>{consumerActivityLabels[data.trip.activity]} · {data.trip.rodCount} {data.trip.rodCount === 1 ? "vapa" : "vapaa"}</em></span>
        </article>
        <article>
          <CloudSun />
          <span><small>Suunnittelussa nähty sää</small><b>{weatherSummary}</b><em>{storedForecast?.capturedAt ? `Ennustekuitti ${new Date(storedForecast.capturedAt).toLocaleString("fi-FI")}` : "Ei korvaa omaa havaintoa"}</em></span>
        </article>
        <article>
          <PackageCheck />
          <span><small>Reissulle pakattu</small><b>{packedTackle.length ? packedTackle.map((item) => `${item.name}${item.quantity > 1 ? ` × ${item.quantity}` : ""}`).join(", ") : "Ei pakatuiksi merkittyjä välineitä"}</b><em>Saalis liitetään käytettyyn välineeseen erikseen</em></span>
        </article>
      </section>
      {completed&&<section className="trip-after-summary"><header><div><span className="eyebrow">Reissun jälkiyhteenveto</span><h4>Suunnitelma ja toteuma rinnakkain</h4></div><strong>{data.journal.rating?`${data.journal.rating}/5`:"Ei arviota"}</strong></header><div><article><small>Suunnittelussa nähty ennuste</small><b>{weatherSummary}</b><p>{storedForecast?.capturedAt?`Tallennettu ${new Date(storedForecast.capturedAt).toLocaleString("fi-FI")}`:"Ennustekuittia ei tallennettu."}</p></article><article><small>Käyttäjän toteutunut havainto</small><b>{data.journal.conditions_notes||"Toteutuneita olosuhteita ei kirjattu."}</b><p>{data.journal.fishing_notes||"Reissumuistiinpanoja ei kirjattu."}</p></article><article><small>Reissun tulos</small><b>{data.journal.no_catch?"Ei saalista":`${data.summary.catches} kalaa`}</b><p>{data.summary.released} vapautettu · {data.catches.filter(item=>item.tackle_name).length} saalismerkintää yhdistetty välineeseen</p></article></div>{data.resultContext.hydrology?.observations.length?<aside className="trip-hydrology-receipt"><CloudSun/><div><small>Lähin SYKE-mittauspiste · ei käyttäjän havainto</small><b>{data.resultContext.hydrology.observations.map(item=>`${item.label} ${item.value.toLocaleString("fi-FI",{maximumFractionDigits:item.kind==="water_level"?2:1})} ${item.unit}${item.referenceSystem?` ${item.referenceSystem}`:""}`).join(" · ")}</b><p>{data.resultContext.hydrology.observations.map(item=>`${item.stationName}, ${new Date(item.observedAt).toLocaleString("fi-FI")}${item.stale?" · vanhentunut":""}`).join(" · ")}</p><a href={data.resultContext.hydrology.sourceUrl} target="_blank" rel="noreferrer">Avaa SYKE-lähde</a></div></aside>:<p className="trip-hydrology-unavailable">Läheistä nimellä täsmäävää SYKE-mittausta ei löytynyt. Olosuhdetta ei päätelty.</p>}<p>Yhteenveto kuvaa vain omaa kirjaustasi. Vedenpinnan korkeusasema ei ole veden syvyys. Ennustetta tai lähiaseman mittausta ei esitetä toteutuneena havaintonasi.</p></section>}
      {data.trip.status === "planned" && (
        <section className="trip-result-choice" aria-label="Valitse reissun tulos">
          <div><span className="eyebrow">1. Valitse tulos</span><h4>Miten reissu meni?</h4><p>Myös saaliiton reissu kuuluu omaan historiaasi ja tekee tilastoista realistisemmat.</p></div>
          <button type="button" className={!noCatch ? "selected" : undefined} onClick={() => setNoCatch(false)}><FishSymbol />Sain saalista</button>
          <button type="button" className={noCatch ? "selected" : undefined} onClick={() => setNoCatch(true)} disabled={data.summary.catches > 0}><span>0</span>Ei saalista</button>
        </section>
      )}
      <form className="trip-journal-notes" onSubmit={(event) => void saveJournal(event)}>
        <label>Reissun arvio<select name="rating" defaultValue={data.journal.rating ?? ""} disabled={!editable}><option value="">Ei arviota</option>{[1,2,3,4,5].map((value)=><option value={value} key={value}>{value}/5</option>)}</select></label>
        <label>Toteutuneet olosuhteet<textarea name="conditionsNotes" rows={3} maxLength={1000} defaultValue={data.journal.conditions_notes} placeholder="Vesi, tuuli, näkyvyys ja omat havainnot" disabled={!editable} /><small>Oma havaintosi pidetään erillään suunnittelun ennusteesta.</small></label>
        <label className="wide-field">Mitä reissulla tapahtui?<textarea name="fishingNotes" rows={5} maxLength={4000} defaultValue={data.journal.fishing_notes} placeholder="Kirjaa muistiin toimineet paikat, ajat ja opit seuraavaa reissua varten" disabled={!editable} /></label>
        <button disabled={busy || !editable}><BookOpen />Tallenna päiväkirja</button>
      </form>
      <section className={`trip-catches${noCatch ? " is-no-catch" : ""}`}>
        <header><div><b>2. Saalismerkinnät</b><small>{noCatch ? "Valitsit saaliittoman reissun – voit siirtyä viimeistelyyn." : "Lisää jokainen laji omana merkintänään ja yhdistä käytetty väline."}</small></div></header>
        {data.catches.length > 0 && <div className="trip-catch-list">{data.catches.map((item)=><article key={item.id}><FishSymbol /><div><b>{item.species_name} · {item.quantity} kpl</b><small>{[item.tackle_name,item.length_mm ? `${item.length_mm} mm` : "",item.weight_g ? `${item.weight_g} g` : "",item.released_count ? `${item.released_count} vapautettu` : "",item.kept_count ? `${item.kept_count} otettu` : ""].filter(Boolean).join(" · ")}</small>{item.notes && <p>{item.notes}</p>}</div><button type="button" aria-label={`Poista ${item.species_name}`} disabled={busy || !editable} onClick={()=>void removeCatch(item.id)}><Trash2 /></button></article>)}</div>}
        {!noCatch && <form className="trip-catch-form" onSubmit={(event)=>void addCatch(event)}>
          <label>Laji<input name="speciesName" maxLength={80} required placeholder="Esimerkiksi ahven" disabled={!editable} /></label>
          <label>Määrä<input name="quantity" type="number" min={1} max={999} defaultValue={1} required disabled={!editable} /></label>
          <label>Vapautettu<input name="releasedCount" type="number" min={0} max={999} defaultValue={0} disabled={!editable} /></label>
          <label>Otettu<input name="keptCount" type="number" min={0} max={999} defaultValue={0} disabled={!editable} /></label>
          <label>Pituus (mm)<input name="lengthMm" type="number" min={1} max={4000} disabled={!editable} /></label>
          <label>Paino (g)<input name="weightG" type="number" min={1} max={100000} disabled={!editable} /></label>
          <label className="wide-field">Saaliseen käytetty väline<select name="tackleItemId" disabled={!editable}><option value="">Ei valintaa</option>{data.tackle.map((item)=><option value={item.id} key={item.id}>{item.name}{item.packed ? " · pakattu mukaan" : " · pakkauslistalla"}</option>)}</select><small>Pakattu väline ei ole automaattisesti saalisväline. Valitse vain se, jolla saalis tuli.</small></label>
          <label className="wide-field">Huomio<input name="notes" maxLength={1000} placeholder="Paikka, kellonaika tai muu hyödyllinen havainto" disabled={!editable} /></label>
          <button disabled={busy || !editable}><Plus />{data.catches.length ? "Lisää toinen laji tai saalis" : "Lisää ensimmäinen saalis"}</button>
        </form>}
      </section>
      {data.trip.status === "planned" && (
        <section className="trip-completion-panel">
          <div>
            <span className="eyebrow">3. Viimeistele</span>
            <h4>Päätä reissu ja vapauta varaukset</h4>
            <p>
              Lisää saalismerkintä tai vahvista saaliiton reissu. Valmiin
              reissun tiedot jäävät omaan historiaasi.
            </p>
          </div>
          <p className="trip-result-confirmation">{noCatch ? "Tallennetaan saaliittomana reissuna." : `${data.summary.catches} kalaa kirjattu. Voit lisätä vielä muita lajeja ennen viimeistelyä.`}</p>
          <button
            type="button"
            disabled={busy || (!noCatch && data.summary.catches < 1)}
            onClick={() => void completeTrip()}
          >
            <BookOpen /> Tallenna ja merkitse reissu valmiiksi
          </button>
        </section>
      )}
      {!editable && <p className="trip-tackle-readonly">Perutun reissun päiväkirja säilyy katseltavana, mutta sitä ei voi muuttaa.</p>}
      <p className="trip-journal-boundary"><LockKeyhole />{data.boundary}</p>
    </section>
  );
}
